import React, {type Dispatch, type SetStateAction,
    useCallback,
    useState,
} from 'react';

import {AccountNameInput} from 'src/components/account-information/components/account-name-input';
import {CountrySuggest} from 'src/components/country-suggest';
import {RequiredMark} from 'src/components/required-mark';
import {SearchTemplateUser} from 'src/components/search-template-user';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl} from 'src/contexts/validation-context';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';


import type {CreateConferenceData} from 'src/modules/create-conference/useCreateConference';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

type Props = {
    conferenceInfo: CreateConferenceData,
    setConferenceInfo: Dispatch<SetStateAction<CreateConferenceData>>,
};

export const AccountInfo = ({conferenceInfo, setConferenceInfo}: Props) => {

    const [templateUser, setTemplateUser] = useState<UserShortInfo | undefined>(undefined);

    const changeAccountName = useCallback((accountName: string) => {
        setConferenceInfo(prevState => ({...prevState, accountName}));
    }, [setConferenceInfo]);

    const changeCountry = useCallback((countryCode: string) => {
        setConferenceInfo(prevState => ({...prevState, countryCode}));
    }, [setConferenceInfo]);

    const changeTemplateUser = useCallback((user: UserShortInfo | undefined) => {
        setTemplateUser(user);
        setConferenceInfo(prevState => ({...prevState, templateId: user?.userId || 0}));
    }, [setConferenceInfo]);

    return (
        <FormSection headline="Account Information">
            <WidthItem width={1100}>
                <Group direction="v">
                    <Group alignItems="start">
                        <WidthItem width={320}>
                            <AccountNameInput
                                id="account-name-input"
                                label={<RequiredMark label="Account name"/>}
                                accountName={conferenceInfo.accountName}
                                onChange={changeAccountName}
                            />
                        </WidthItem>
                        <WidthItem width={320}>
                            <Label htmlFor="entity-type-combo-box">Entity type</Label>
                            <ComboBox id="entity-type-combo-box" value="event" disabled={true}>
                                <ComboboxItem value="event" caption="Event"/>
                            </ComboBox>
                        </WidthItem>
                    </Group>
                    <Group alignItems="start">
                        <ValidatedControl name="account-template-user">
                            {invalid => (
                                <SearchTemplateUser
                                    width={320}
                                    placeholder="Select user"
                                    label={<RequiredMark label="Create new users based on user"/>}
                                    selectedTemplateUser={templateUser}
                                    onSelect={changeTemplateUser}
                                    color={invalid ? 'ERROR' : undefined}
                                />
                            )}
                        </ValidatedControl>
                        <ValidatedControl name="account-country-select">
                            {invalid => (
                                <WidthItem width={320}>
                                    <Label htmlFor="country-suggest"><RequiredMark label="Country"/></Label>
                                    <CountrySuggest
                                        selectedCountry={conferenceInfo.countryCode}
                                        onSelect={changeCountry}
                                        error={invalid}
                                    />
                                </WidthItem>
                            )}
                        </ValidatedControl>
                    </Group>
                </Group>
            </WidthItem>
        </FormSection>
    );
};
